/**
 * Hook for permission checks
 *
 * Permission evaluation is performed server-side by the Casbin engine via the
 * CheckMyPermissions API. The frontend simply looks up the result in a map.
 * This eliminates the previous bugs where the frontend duplicated Casbin logic
 * incorrectly (single-role bug, missing scope hierarchy, no domain isolation).
 */

import apiPermissions from '@compass/client/constants/api-permissions.json';
import { useStore } from '@store/index';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { membershipService } from '@/services/api';
import { isUserAssigned } from '@/utils/assignment';
import { isCaseProgramAdmin } from '@/utils/caseActions';
import { filterRolesByContext } from '@/utils/user';

import { useProgramTeams } from './useTeams';
import { useCurrentUserRoles, useMyRoles } from './useUsers';

/**
 * Level hierarchy mapping. Higher levels grant access to lower levels.
 * global (3) > organization (2) > program (1)
 *
 * Can be used for:
 * - Permission checking: userLevel >= requiredLevel
 * - Sorting (descending): higher value = higher priority (global first)
 */
function getLevelValue(level?: string): number {
  switch (level) {
    case 'global':
      return 3;
    case 'organization':
      return 2;
    case 'program':
      return 1;
    default:
      return 0;
  }
}

/**
 * Hook to get the current user's roles filtered by organization and program context.
 *
 * This hook:
 * 1. Fetches roles using useCurrentUserRoles (which also stores them in Zustand)
 * 2. Filters them based on the selected organization and program from store
 * 3. Sorts roles by level: global > organization > program
 * 4. Returns roles with exact match (org + program) or org-level match
 *
 * @returns Object containing filtered roles, permissions, and loading state
 */
export const useCurrentUserContextRoles = () => {
  const { selectedOrganizationId, selectedProgramId } = useStore();

  // Fetch user roles - this also stores them in the Zustand store
  const {
    data: rolesData,
    isLoading,
    error,
  } = useCurrentUserRoles(selectedOrganizationId ?? undefined, selectedProgramId ?? undefined);

  // Filter and sort roles based on context
  const contextRoles = useMemo(() => {
    const roles = rolesData?.user_roles || [];
    const filtered = filterRolesByContext(roles, selectedOrganizationId, selectedProgramId);
    // Sort by level: global > organization > program (descending by getLevelValue)
    return filtered.sort((a, b) => getLevelValue(b.level) - getLevelValue(a.level));
  }, [rolesData?.user_roles, selectedOrganizationId, selectedProgramId]);

  // Extract all permissions from context roles
  const permissions = useMemo(() => {
    const allPermissions = contextRoles.flatMap(role => role.permissions || []);
    return allPermissions;
  }, [contextRoles]);

  // Get role names for debugging/display
  const roleNames = useMemo(() => {
    return contextRoles.map(role => role.name).filter(Boolean);
  }, [contextRoles]);

  return {
    roles: contextRoles,
    permissions,
    roleNames,
    hasRoles: contextRoles.length > 0,
    isLoading,
    error,
  };
};

/**
 * Hook to check if the current user is assigned to a resource.
 *
 * Compares by the canonical `assigned_user_id` when available, falling back to the
 * deprecated `assigned` email. The email field is denormalized and can go stale
 * (e.g. after an email change, or when case-dispatch sets only the id), so it must
 * never override a definitive id comparison. See {@link isUserAssigned}.
 *
 * @returns A function `(assignedEmail, assignedUserId?) => boolean`.
 */
export function useIsCurrentUserAssigned() {
  const currentUserEmail = useStore(state => state.user?.email);
  const currentUserId = useStore(state => state.user?.id);

  return (
    assignedEmail: string | null | undefined,
    assignedUserId?: string | null | undefined,
  ): boolean =>
    isUserAssigned({
      currentUserId,
      currentUserEmail,
      assignedUserId,
      assignedEmail,
    });
}

/**
 * Hook to check if the current user is a team lead (manager) for the selected program.
 * Checks if the user's ID matches any team's manager_id in the program's teams.
 */
export function useIsTeamLead(): boolean {
  const currentUserId = useStore(state => state.user?.id);
  const { teams } = useProgramTeams();

  return useMemo(() => {
    if (!currentUserId || teams.length === 0) return false;
    return teams.some(team => team.manager_id === currentUserId);
  }, [currentUserId, teams]);
}

/**
 * Hook to check whether the current user is a Program Admin of a case or holds a role
 * above it (BV-2514, BV-2657), the same rule the server enforces for "Move to Human Audit"
 * on transportation and unifiedmedbv.
 * Reads every role the user holds, not just the selected program's, so it also works
 * on views that list cases from several programs.
 */
export function useIsCaseProgramAdmin(
  organizationId?: string | null,
  programId?: string | null,
): boolean {
  const { data } = useMyRoles();

  return useMemo(
    () => isCaseProgramAdmin(data?.user_roles, organizationId ?? undefined, programId ?? undefined),
    [data?.user_roles, organizationId, programId],
  );
}

// ============================================================================
// API Permission Checks (server-side via CheckMyPermissions)
// ============================================================================

// Type-safe API method keys
export type APIMethod = keyof typeof apiPermissions;

interface PermissionCheck {
  hasPermission: boolean;
}

/**
 * Query key for the permission map cache.
 * Invalidated when roles change (add/remove/change).
 */
export const permissionMapQueryKey = ['compass', 'permissionMap'] as const;

interface PermissionMapContextOverride {
  organizationId?: string | null;
  programId?: string | null;
}

/**
 * Hook that fetches the permission map from the backend.
 * The backend evaluates all API method permissions using the real Casbin engine,
 * returning a map of method path -> boolean.
 *
 * This replaces the old local Casbin-like evaluation that had bugs:
 * - Single-role bug (only checked first role)
 * - Missing scope hierarchy
 * - No domain isolation
 * - Incomplete action matching
 */
export function usePermissionMap(contextOverride: PermissionMapContextOverride = {}) {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);
  const effectiveOrganizationId =
    'organizationId' in contextOverride ? contextOverride.organizationId : selectedOrganizationId;
  const effectiveProgramId =
    'programId' in contextOverride ? contextOverride.programId : selectedProgramId;

  return useQuery({
    queryKey: [
      ...permissionMapQueryKey,
      effectiveOrganizationId ?? 'no-org',
      effectiveProgramId ?? 'no-program',
    ] as const,
    queryFn: () =>
      membershipService.checkMyPermissions({
        organization_id: effectiveOrganizationId ?? undefined,
        program_id: effectiveProgramId ?? undefined,
      }),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    enabled: !!effectiveOrganizationId,
  });
}

/**
 * Hook to check if the current user has permission to call a specific API method or methods.
 * Uses the server-side permission map for accurate Casbin evaluation.
 *
 * @param apiMethod - Single API method key or array of API method keys
 * @param requirementType - When multiple methods provided: 'any' (OR) or 'all' (AND) logic
 * @returns Permission check result with hasPermission flag
 *
 * @example Single method
 * ```tsx
 * const check = useAPIPermission(API_METHODS.CaseRecordsService.CreateCaseRecord);
 * if (check.hasPermission) { ... }
 * ```
 *
 * @example Multiple methods with OR logic
 * ```tsx
 * const check = useAPIPermission([
 *   API_METHODS.CaseRecordsService.UpdateCaseRecord,
 *   API_METHODS.CaseRecordsService.DeleteCaseRecord,
 * ], 'any');
 * ```
 */
export function useAPIPermission(
  apiMethod: APIMethod | APIMethod[],
  requirementType: 'any' | 'all' = 'all',
): PermissionCheck {
  const { data, isLoading } = usePermissionMap();

  return useMemo(() => {
    const permissions = data?.permissions;
    if (isLoading || !permissions) {
      return { hasPermission: false };
    }

    const methods = Array.isArray(apiMethod) ? apiMethod : [apiMethod];

    // Convert APIMethod keys (e.g., "CaseRecordsService.CreateCaseRecord")
    // to full gRPC method paths (e.g., "/compasspb.CaseRecordsService/CreateCaseRecord")
    // that the backend uses as map keys.
    const hasPermission =
      requirementType === 'any'
        ? methods.some(m => lookupPermission(permissions, m))
        : methods.every(m => lookupPermission(permissions, m));

    return { hasPermission };
  }, [apiMethod, requirementType, data, isLoading]);
}

/**
 * Looks up a permission in the backend map.
 * The backend returns keys in "ServiceName.MethodName" format (e.g., "CaseRecordsService.CreateCaseRecord"),
 * which matches the frontend APIMethod constants directly.
 */
function lookupPermission(permissions: Record<string, boolean>, apiMethod: string): boolean {
  return permissions[apiMethod] === true;
}

/**
 * Hook to simply check if the current user can call a specific API method.
 *
 * @param apiMethod - API method key
 * @returns boolean indicating if user has permission
 *
 * @example
 * ```tsx
 * import { useCanCallAPI, API_METHODS } from '@hooks/usePermissions';
 *
 * const canDelete = useCanCallAPI(API_METHODS.CaseRecordsService.DeleteCaseRecord);
 *
 * function handleDelete() {
 *   if (!canDelete) {
 *     toast.error('You do not have permission to delete cases');
 *     return;
 *   }
 *   // Proceed with deletion
 * }
 * ```
 */
export function useCanCallAPI(apiMethod: APIMethod): boolean {
  const check = useAPIPermission(apiMethod);
  return check.hasPermission;
}

/**
 * Hook for controlling UI element state based on API permissions.
 * Useful for disabling buttons, showing tooltips, etc.
 *
 * @param apiMethod - API method key
 * @returns Object with canAccess, disabled, tooltip, and permission details
 *
 * @example
 * ```tsx
 * import { usePermissionGate, API_METHODS } from '@hooks/usePermissions';
 *
 * function DeleteButton({ caseId }: { caseId: string }) {
 *   const { disabled, tooltip } = usePermissionGate(
 *     API_METHODS.CaseRecordsService.DeleteCaseRecord
 *   );
 *
 *   return (
 *     <Tooltip content={tooltip}>
 *       <Button disabled={disabled} onClick={() => handleDelete(caseId)}>
 *         Delete Case
 *       </Button>
 *     </Tooltip>
 *   );
 * }
 * ```
 */
export function usePermissionGate(apiMethod: APIMethod) {
  const check = useAPIPermission(apiMethod);

  return {
    canAccess: check.hasPermission,
    disabled: !check.hasPermission,
    tooltip: 'You have permission to perform this action',
  };
}
